import { Flex, Spin } from "antd";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import chartIcon from "@/assets/auth/auth-chart.svg";
import passwordIcon from "@/assets/auth/auth-password.svg";
import lumianTextWhite from "@/assets/lumian/lumian-text-white.svg";
import LogoutButton from "@/components/LogoutButton";
import { NAMESPACES } from "@/constants/i18next.constants";
import { ROUTES } from "@/constants/routes";
import LanguagePicker from "@/containers/LanguagePicker";

import { COMPANIES } from "./AuthenticationLayout.constants";
import {
  CompanyFigure,
  Container,
  LeftContainer,
  LeftContent,
  ParagraphStyled,
  RightContainer,
  RightContent,
  RightFooter,
  RightFooterContent,
  RightIcon,
  RightLogo,
  TitleStyled,
} from "./AuthenticationLayout.styles";

function AuthenticationLayout() {
  const { t } = useTranslation(NAMESPACES.AUTH);
  const { pathname } = useLocation();

  const backgroundImage = useMemo(() => {
    if (
      ([ROUTES.FORGOT_PASSWORD, ROUTES.RESET_PASSWORD] as string[]).includes(
        pathname,
      )
    ) {
      return passwordIcon;
    }
    return chartIcon;
  }, [pathname]);

  return (
    <Container>
      <LeftContainer>
        <LeftContent>
          <Flex justify="space-between">
            <LanguagePicker />
            {ROUTES.RESET_PASSWORD === pathname && <LogoutButton />}
          </Flex>

          <Suspense fallback={<Spin />}>
            <Outlet />
          </Suspense>
        </LeftContent>
      </LeftContainer>
      <RightContainer>
        <RightIcon
          key={backgroundImage}
          initial={{
            opacity: 0,
            y: 100,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.5,
          }}
          src={backgroundImage}
          alt=""
        />
        <RightLogo src={lumianTextWhite} alt="Digiterra logo" width={100} />
        <RightContent>
          <TitleStyled level={5} variant="xlarge" weight="semiBold">
            {t("slogan")}
          </TitleStyled>
          <ParagraphStyled variant="semiMedium">
            {t("sloganDescription")}
          </ParagraphStyled>
        </RightContent>
        <RightFooter>
          <RightFooterContent>
            {COMPANIES.map(company => (
              <CompanyFigure key={company.alt}>
                <img src={company.imgUrl} alt={company.alt} />
              </CompanyFigure>
            ))}
          </RightFooterContent>
        </RightFooter>
      </RightContainer>
    </Container>
  );
}

export default AuthenticationLayout;
